import React, { useEffect, useMemo, useState } from 'react';
import './CreativeLoader.css';

interface CreativeLoaderProps {
  message?: string;
  size?: 'small' | 'medium' | 'large';
  showJoke?: boolean;
}

const workplaceJokes = [
  // Tech jokes
  "Why do programmers prefer dark mode? Because light attracts bugs! 🐛",
  "Why did the developer go broke? Because he used up all his cache 💸",
  "Why do Java developers wear glasses? Because they don't C# 🤓",
  "Pro tip: Ctrl+Z works in code, not in life 😅",
  "Why don't programmers like nature? It has too many bugs 🌳",
  "Why was the computer cold? It left its Windows open 🪟",
  "Why did the developer quit his job? He didn't get arrays 📊",
  "There are 10 types of people: those who understand binary and those who don't 💻",
  "Why did the function break up with the variable? Too many arguments! 💔",
  "404 Error: Motivation not found 🔍",
  "Git commit -m 'Fixed bug' // Narrator: The bug was not fixed 🐛",
  "Stack Overflow: Where copy-paste becomes an art form 🎨",

  // General workplace humor
  "Coffee: Turning 'I can't' into 'I did' since forever ☕",
  "Fun fact: The average person spends 90,000 hours at work in their lifetime 🕐",
  "Dress code: Business casual... or pajamas if the camera's off 👔",
  "Friday is the most productive day... for planning the weekend 📅",
  "Why did the scarecrow get promoted? He was outstanding in his field 🌾",
  "The word 'job' is only 3 letters but feels like a lifetime 💼",
  "Why did the employee get fired from the calendar factory? He took a day off 📆",
  "Teamwork: A chance to blame someone else 🤝",
  "Success is 1% inspiration, 99% coffee ☕",
  "Why did the PowerPoint presentation cross the road? To get to the other slide 📊",
  "Monday called. It wants its weekend back 😴",
  "Fun fact: 60% of meeting time could have been an email 📧",
  "Work-life balance: A myth we tell ourselves 🎭",

  // Job search & career humor
  "Looking for a job is a full-time job... that doesn't pay 😅",
  "Fun fact: 85% of jobs are filled through networking, not job boards 🤝",
  "Resume tip: 'Proficient in Microsoft Word' = I can make the font bigger 📄",
  "Interview wisdom: Dress for the job you want, not the pajamas you're wearing 👗",
  "Why did the job seeker bring a ladder to the interview? To reach new heights! 🪜",
  "Networking: The art of meeting people who won't remember you tomorrow 🎯",
  "Fun fact: The average job search takes 5 months... but feels like 5 years ⏰",
  "Cover letters: Where you lie about reading the company's mission statement 📝",
  "LinkedIn: Where everyone's a CEO, visionary, or thought leader 💭",
  "Job description: Must be entry-level with 10 years of experience 🤔",
  "Salary expectations: Whatever keeps me from eating ramen every night 🍜",
  "Thank you email after interview: Please hire me, I'm desperate (but professional) 💼",
  "References available upon request = I need time to warn my friends 📞",
  "Why did the candidate bring a map to the interview? To show career direction! 🗺️",
  "Entry-level position: 5 years experience required. Make it make sense 🤷",
  "Auto-reply: Your application has been received. Translation: Into the void 📫",
  "Ghosted after 5 rounds of interviews? That's a pro recruiter move 👻",
  "Why was the job application sad? It got auto-rejected in 0.3 seconds ⚡",
  "Skills: Overqualified for jobs I want, underqualified for jobs I get 📊",
  "Fun fact: Recruiters spend more time on your LinkedIn photo than experience 📸",
  "Interviews: Where 'Tell me about yourself' causes an existential crisis 🤔",
  "Applied to 100 jobs, heard back from 0. At least I'm consistent! 📉",
  "Why did the resume go to therapy? It had too many issues! 😄",
  "Job hunting tip: Coffee is a food group during unemployment ☕",

  // Office culture
  "The best time to start networking was yesterday. The second best time is now 🌱",
  "Fun fact: Americans check work email an average of 15 times per day 📱",
  "Why did the manager bring a ladder to the meeting? For higher-level discussions! 🎯",
  "Office plants: The only colleagues who appreciate your presence 🌿",
  "The printer knows when you're in a hurry... that's when it jams 🖨️",
  "Meeting invitation: 'Quick 15-min sync' = Actually an hour-long debate ⏱️",
  "Reply all: The workplace equivalent of yelling in a crowded room 📢",
  "Out of office: The two most beautiful words in the English language 🏖️"
];

const CreativeLoader: React.FC<CreativeLoaderProps> = ({
  message = 'Loading...',
  size = 'medium',
  showJoke = true
}) => {
  const sizeMap = {
    small: { container: 40, dot: 10 },
    medium: { container: 60, dot: 14 },
    large: { container: 80, dot: 18 }
  };

  const dimensions = sizeMap[size];

  // Start with a random joke, then cycle through them
  const [currentJokeIndex, setCurrentJokeIndex] = useState(() =>
    Math.floor(Math.random() * workplaceJokes.length)
  );
  const [isFading, setIsFading] = useState(false);

  // Change joke every 8 seconds with fade-out/fade-in effect
  useEffect(() => {
    if (!showJoke) return;

    const interval = setInterval(() => {
      // Start fade out
      setIsFading(true);

      // After fade out completes, change joke and fade in
      setTimeout(() => {
        setCurrentJokeIndex((prevIndex) => (prevIndex + 1) % workplaceJokes.length);
        setIsFading(false);
      }, 700); // Wait for fade-out to complete
    }, 8000);

    return () => clearInterval(interval);
  }, [showJoke]);

  const currentJoke = workplaceJokes[currentJokeIndex];

  return (
    <div className="creative-loader-container">
      <div
        className="creative-loader"
        style={{
          width: `${dimensions.container}px`,
          height: `${dimensions.container}px`
        }}
      >
        <div
          className="loader-dot dot-1"
          style={{
            width: `${dimensions.dot}px`,
            height: `${dimensions.dot}px`
          }}
        ></div>
        <div
          className="loader-dot dot-2"
          style={{
            width: `${dimensions.dot}px`,
            height: `${dimensions.dot}px`
          }}
        ></div>
        <div
          className="loader-dot dot-3"
          style={{
            width: `${dimensions.dot}px`,
            height: `${dimensions.dot}px`
          }}
        ></div>
        <div
          className="loader-dot dot-4"
          style={{
            width: `${dimensions.dot}px`,
            height: `${dimensions.dot}px`
          }}
        ></div>
      </div>
      <div className="loader-text-content">
        {message && (
          <p className="loader-message" style={{ fontSize: size === 'small' ? '13px' : size === 'large' ? '17px' : '15px' }}>
            {message}
          </p>
        )}
        {showJoke && (
          <p
            className={`loader-joke ${isFading ? 'fading' : ''}`}
            style={{ fontSize: size === 'small' ? '12px' : size === 'large' ? '15px' : '13px' }}
          >
            {currentJoke}
          </p>
        )}
      </div>
    </div>
  );
};

export default CreativeLoader;
